import classNames from "classnames";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import { env } from "../../../environment";
import { CartContext } from "../../contexts/CartContext";
import { ThemeContext } from "../../contexts/ThemeContext";
import { hasShippingMethodConflict } from "../../ecommerce/cart";
import { getHighlightElement } from "../../lib/intl-utils";
import LoadingSpinner from "../LoadingSpinner";
import Money from "../Money";

import css from "./CartSummary.module.scss";

export const TEST_ID_CART_SUMMARY = "cart-summary";
export const TEST_ID_COMPARE_PRICE_TOTAL = "cart-summary-compare-price-total";
export const TEST_ID_DISCOUNT_VALUE = "cart-summary-discount-value";
export const TEST_ID_TOTAL_PRICE = "cart-summary-total-price";
export const TEST_ID_CHECKOUT_BUTTON_WITHOUT_SIGNING_IN =
  "cart-summary-checkout-button-without-signing-in";
export const TEST_ID_MAINTENANCE_TEXT = "cart-summary-maintenance-text";
export const TEST_ID_PAST_ITEMS_WARNING = "cart-summary-past-items-warning";

function getProceedButtonText(isUpdating: boolean): string {
  if (isUpdating) {
    return "status.updatingProductToCart";
  }
  return "cart.proceedToPay";
}

export interface CartSummaryProps {
  checkoutFn: (checkoutUrl: string) => void;
  error?: string | undefined;
  hasPastItems: boolean;
  isCartValidated: boolean;
}

const CartSummary: React.FC<CartSummaryProps> = ({
  checkoutFn,
  error,
  hasPastItems,
  isCartValidated,
}) => {
  const { formatMessage } = useIntl();
  const {
    checkoutUrl,
    cartItems,
    isLoading,
    isTouched,
    isAddingLines,
    isRemovingLines,
    locale,
    summaries,
    update,
  } = useContext(CartContext);
  const { styles } = useContext(ThemeContext);

  const [isProceedingToPay, setIsProceedingToPay] = useState(false);

  useEffect(() => {
    const clearStateForSafari = () => {
      setIsProceedingToPay(false);
    };
    window?.addEventListener("pageshow", clearStateForSafari);
    return () => {
      window?.removeEventListener("pageshow", clearStateForSafari);
    };
  }, []);

  const goToCheckout = useCallback(async () => {
    if (env.MAINTENANCE) {
      return;
    }

    if (typeof error === "string") {
      toast.error(formatMessage({ id: error }));
      return;
    }

    if (checkoutUrl) {
      setIsProceedingToPay(true);

      const canProceed = !isTouched || (await update({ openMiniCart: false }));
      if (canProceed) {
        if (hasShippingMethodConflict(cartItems)) {
          toast.error(formatMessage({ id: "error.commonShippingMethod" }));
          setIsProceedingToPay(false);
        } else {
          const checkoutWithLocale = new URL(checkoutUrl);
          if (locale !== undefined) {
            checkoutWithLocale.searchParams.set("locale", locale);
          }
          checkoutFn(checkoutWithLocale.toString());
        }
      } else {
        setIsProceedingToPay(false);
      }
    }
  }, [
    checkoutUrl,
    cartItems,
    error,
    isTouched,
    locale,
    update,
    formatMessage,
    checkoutFn,
  ]);

  if (isLoading) {
    return null;
  }

  const isProceedDisabled =
    isAddingLines ||
    isRemovingLines ||
    !isCartValidated ||
    hasPastItems ||
    env.MAINTENANCE;

  const proceedToPaymentButton = (
    <button
      onClick={goToCheckout}
      className={classNames(
        "mt3",
        {
          [styles.primaryButton]: !isProceedDisabled,
          [styles.primaryButtonDisabled]: isProceedDisabled,
        },
        "w-100",
      )}
      disabled={isProceedDisabled}
      data-testid={TEST_ID_CHECKOUT_BUTTON_WITHOUT_SIGNING_IN}
    >
      <FormattedMessage
        id={getProceedButtonText(isAddingLines || isRemovingLines)}
      />
    </button>
  );

  return (
    <div className="bg-white ph3 h-100" data-testid={TEST_ID_CART_SUMMARY}>
      <div className={classNames("pv4", css.stickyCartSummary)}>
        <h2 className={`${styles.heading1} mt0 mb5 dn db-ns`}>
          <FormattedMessage id="cart.summary" />
        </h2>
        <table className="w-100">
          <tbody>
            <tr className={styles.heading3}>
              <td className="pb4">
                <FormattedMessage id="cart.orderValue" />
              </td>
              <td className="pb4 tr" data-testid={TEST_ID_COMPARE_PRICE_TOTAL}>
                <Money value={summaries.local.comparePriceTotal} />
              </td>
            </tr>
            {summaries.local.comparePriceDiscount > 0 && (
              <tr className={styles.heading3}>
                <td className="pb4">
                  <FormattedMessage id="cart.save" />
                </td>
                <td className="pb4 tr" data-testid={TEST_ID_DISCOUNT_VALUE}>
                  <Money value={-summaries.local.comparePriceDiscount} />
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className={styles.heading2}>
              <td>
                <FormattedMessage id="cart.total" />
              </td>
              <td className="tr" data-testid={TEST_ID_TOTAL_PRICE}>
                <Money value={summaries.local.totalPrice} />
              </td>
            </tr>
          </tfoot>
        </table>
        <div className="mt5">
          {env.MAINTENANCE && (
            <p data-testid={TEST_ID_MAINTENANCE_TEXT}>
              <FormattedMessage
                id="maintenance.purchasingDisabled"
                values={{ strong: getHighlightElement }}
              />
            </p>
          )}

          {isProceedingToPay ? (
            <LoadingSpinner
              message={formatMessage({ id: "cart.proceedingToPay" })}
            />
          ) : (
            <div className="bt b--moon-gray mb4">
              {proceedToPaymentButton}
              {hasPastItems && (
                <p
                  className="lh-copy red"
                  data-testid={TEST_ID_PAST_ITEMS_WARNING}
                >
                  <strong>
                    <FormattedMessage id="cart.pastItemsInCart" />
                  </strong>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartSummary;
